import { fg } from '@atlaskit/platform-feature-flags';
import type { FilterId } from '@atlassian/rovo-connectors-config/types/filters';

import { DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI } from '../../default-cloud-graph-document-entity-ati';
import { DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI } from '../../default-cloud-graph-remote-link-entity-ati';
import { DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI } from '../../default-cloud-graph-video-entity-ati';
import {
	NounSubtypeKey,
	type TypeFilterValue3PEntities,
	TypeFilterValueKey,
} from '../../filters/universal-type';
import { get3PLogo } from '../../icons';
import { type ThirdPartyConfig } from '../../schemas/3p-config';

import { GoogleDriveIcon } from './icon';
import { GoogleDriveIconBorderless } from './icon-borderless';
import { GoogleDriveSearchResultIcon } from './search-result-icon';

const getGoogleDriveTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	config.set(TypeFilterValueKey.Document, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: fg('rovo_fps_enable_google_drive_new_types_filter')
			? [NounSubtypeKey.Document, NounSubtypeKey.Pdf, NounSubtypeKey.Page, NounSubtypeKey.Blogpost]
			: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
	});

	config.set(TypeFilterValueKey.Spreadsheet, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Spreadsheet],
	});

	config.set(TypeFilterValueKey.Presentation, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Presentation],
	});

	if (fg('rovo_fps_enable_video_noun_filter')) {
		config.set(TypeFilterValueKey.Video, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI],
			subTypes3P: [NounSubtypeKey.Video],
		});
	}

	if (fg('rovo_fps_enable_google_drive_new_types_filter')) {
		config.set(TypeFilterValueKey.Image, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Image],
		});
		config.set(TypeFilterValueKey.Folder, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Folder],
		});
		config.set(TypeFilterValueKey.Audio, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Audio],
		});
		config.set(TypeFilterValueKey.Pdf, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Pdf],
		});
		config.set(TypeFilterValueKey.Code, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Code],
		});
		config.set(TypeFilterValueKey.Form, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Form],
		});
		config.set(TypeFilterValueKey.Notebook, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Notebook],
		});

		config.set(TypeFilterValueKey.WebPage, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.WebPage, NounSubtypeKey.Shortcut],
		});
		config.set(TypeFilterValueKey.Other, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [NounSubtypeKey.Other, NounSubtypeKey.Archive],
		});
	}

	// Comments are embedded within documents as linkedEntities, not standalone entities.
	// Keep the entity type aligned to document results so any consumer of typeFilterConfig still
	// treats Google Drive comment matches as documents rather than standalone comments.
	if (fg('enable_3p_embedded_comments_search')) {
		config.set(TypeFilterValueKey.Comment, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [],
		});
	}

	return config;
};

// static 3P config object for google drive
export const googleDriveConfig: ThirdPartyConfig = {
	displayName: 'Google Drive',
	id: 'drive',
	resourceType: 'google-drive-connector',
	icon: get3PLogo(GoogleDriveIcon, 'Google Drive', 'medium'),
	iconBorderless: get3PLogo(GoogleDriveIconBorderless, 'Google Drive', 'small'),
	searchResultIcon: get3PLogo(GoogleDriveSearchResultIcon, 'Google Drive', 'large'),
	integrationAri: 'ari:cloud:platform::integration/google',
	// Use a getter to evaluate lazily so that it's evaluated when accessed, not at module load.
	// This allows for feature gate mocks in tests to work correctly
	get typeFilterConfig() {
		return getGoogleDriveTypeFilterConfig();
	},
	get filters(): FilterId[] {
		const baseFilters: FilterId[] = ['lastUpdated', 'onlyTitleMatch'];
		if (fg('rovo_fps_enable_gdrive_space_filter')) {
			baseFilters.push('space');
		}
		if (fg('rovo_fps_enable_gdrive_content_filter')) {
			baseFilters.push('content');
		}
		return baseFilters;
	},
	requiresOauth: true,
	isEnabled: () => true,
};
